/* @flow */
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import { sortBy, reverse } from 'lodash';
import idx from 'idx';

import { showToast, showAPIErrorToast } from 'reducers/toasts';
import { withRouter } from 'app/context/Router.jsx';
import ADMIN_DISTRICT_SIS_DASHBOARD_QUERY from 'admin/queries/AdminDistrictSisDashboardQuery.gql';
import AdminDistrictSisDashboardCleverHeader from 'admin/components/AdminDistrictSisDashboardCleverHeader.jsx';
import AdminDistrictSisDashboardSftpHeader from 'admin/components/AdminDistrictSisDashboardSftpHeader.jsx';
import awaitData from 'lib/awaitData';
import Divider from 'components/Divider.jsx';
import Link from 'components/Link.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import Row from 'components/Row.jsx';
import SmartTable from 'components/SmartTable.jsx';
import StatusText from 'components/StatusText.jsx';
import TableCell from 'components/TableCell.jsx';
import TableRow from 'components/TableRow.jsx';
import Text from 'components/Text.jsx';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Time from 'components/Time.jsx';

import type {
  AdminDistrictSisDashboard_organizations,
  AdminDistrictSisDashboard_organizations_childOrganizations,
} from 'admin/queries/__generated__/AdminDistrictSisDashboard';

import './styles/AdminDistrictSisDashboard.scss';

type Props = {
  router: *,
  orgId: number,
  syncMethod: string,
  organization: AdminDistrictSisDashboard_organizations,
};

const nameSorts = [
  { key: 'name_asc', name: 'Ascending' },
  { key: 'name_desc', name: 'Descending' },
];

const errorSorts = [
  { key: 'error_asc', name: 'Ascending' },
  { key: 'error_desc', name: 'Descending' },
];

const alertSorts = [
  { key: 'alert_asc', name: 'Ascending' },
  { key: 'alert_desc', name: 'Descending' },
];

const statusSorts = [
  { key: 'status_asc', name: 'Ascending' },
  { key: 'status_desc', name: 'Descending' },
];

const errorTooltip =
  "Errors are mistakes in your SIS that prevent student or teacher accounts from being created. It's critical you address these as soon as possible.";
const alertTooltip =
  'Alerts are minor data errors in your SIS that, if fixed, would make Remind more useful and productive to use.';

const COLUMNS = [
  { key: 'name', name: 'School', sorts: nameSorts },
  { key: 'teachers', name: 'Teachers' },
  { key: 'students', name: 'Students' },
  { key: 'errors', name: 'Errors', sorts: errorSorts, tooltip: errorTooltip },
  { key: 'alerts', name: 'Alerts', sorts: alertSorts, tooltip: alertTooltip },
  { key: 'sync_status', name: 'Sync status', sorts: statusSorts },
];

export function sortOrgs(
  orgs: $ReadOnlyArray<?AdminDistrictSisDashboard_organizations_childOrganizations>,
  sort?: string
) {
  let iteratees = [
    o => o.sisSyncOverview.syncStatus === 'on_hold',
    o => o.sisSyncOverview.errorCount,
    o => o.sisSyncOverview.syncStatus !== 'not_set_up',
  ];
  let descending = true;

  if (sort) {
    if (sort.endsWith('_asc')) {
      descending = false;
    }

    if (sort.startsWith('name_')) {
      iteratees = [o => o.name];
    } else if (sort.startsWith('error_')) {
      iteratees = [o => o.sisSyncOverview.errorCount];
    } else if (sort.startsWith('alert_')) {
      iteratees = [o => o.sisSyncOverview.alertCount];
    } else if (sort.startsWith('status_')) {
      iteratees = [o => o.sisSyncOverview.syncStatus];
    }
  }

  const sortedOrgs = sortBy(orgs, iteratees);
  return descending ? reverse(sortedOrgs) : sortedOrgs;
}

class AdminDistrictSisDashboard extends React.Component<Props> {
  renderErrorName = (name: string) => {
    return (
      <div className="AdminDistrictSisDashboard-errorName">
        <TextWithGlyph
          glyph="explanation-circle"
          glyphColor="lava"
          glyphSize="large"
          gutter="xsmall"
          size="xxsmall"
          color="lava"
          weight="semibold">
          {name}
        </TextWithGlyph>
      </div>
    );
  };

  renderSyncStatus = (status: string) => {
    if (status === 'not_set_up') {
      return 'Set up sync';
    }
    return (
      <StatusText success={status === 'connected'} failure={status === 'on_hold'}>
        {status.replace('_', ' ')}
      </StatusText>
    );
  };

  renderHeader() {
    const { organization, syncMethod } = this.props;

    const headerTitle = 'SIS sync';
    const headerSubtitle = 'Select a school to view its details.';

    if (syncMethod === 'Clever') {
      return (
        <AdminDistrictSisDashboardCleverHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          organization={organization}
        />
      );
    } else if (syncMethod === 'SFTP') {
      return (
        <AdminDistrictSisDashboardSftpHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          organization={organization}
        />
      );
    } else {
      return null;
    }
  }

  renderLastUploadInfo() {
    const { organization } = this.props;
    const lastUploadTimestamp = idx(
      organization,
      _ => _.sisSyncOverview.districtStatus.lastUploadTimestamp
    );
    const lastUploadFiles = idx(
      organization,
      _ => _.sisSyncOverview.districtStatus.lastUploadFiles
    );
    const filesZipUrl = idx(organization, _ => _.sisSyncOverview.districtStatus.filesZipUrl);

    if (!lastUploadTimestamp || !lastUploadFiles) {
      return null;
    }

    const time = (
      <Time
        date={new Date(lastUploadTimestamp)}
        month="long"
        day="numeric"
        hour12
        hour="numeric"
        minute="2-digit"
      />
    );
    const files = lastUploadFiles.join(', ');

    return (
      <Row size="xsmall">
        <Divider size="xsmall" />
        <Text>
          Last files received {time}: {files}.{' '}
        </Text>
        <Link weight="semibold" href={filesZipUrl || '#'}>
          Download all CSVs.
        </Link>
      </Row>
    );
  }

  renderBody() {
    const { organization, orgId } = this.props;

    return (
      <section className="AdminAdminsView">
        {this.renderHeader()}
        {this.renderLastUploadInfo()}
        <SmartTable columns={COLUMNS} namespace={`admin-sis-dashboard-school-list-${orgId}`}>
          {({ sort }) => {
            return (
              <tbody>
                {sortOrgs(organization.childOrganizations, sort).map(child => {
                  if (!child.paidAt) {
                    return null;
                  }
                  const syncStatus = child.sisSyncOverview.syncStatus || '';
                  const isOnHold = syncStatus === 'on_hold';
                  const hasErrors = child.sisSyncOverview.errorCount > 0;
                  const notSetUp = syncStatus === 'not_set_up';
                  const noData = syncStatus === 'processing' || notSetUp;
                  const onRowClick = () => {
                    if (!notSetUp) {
                      this.props.router.push({
                        pathname: `/organizations/${child.id}/settings/organization/sis_sync`,
                      });
                    }
                  };
                  return (
                    <TableRow hoverable key={child.uuid} onClick={onRowClick}>
                      <td>
                        {hasErrors || isOnHold ? this.renderErrorName(child.name) : child.name}
                      </td>
                      <TableCell empty={noData}>{child.sisSyncOverview.teacherCount}</TableCell>
                      <TableCell empty={noData}>{child.sisSyncOverview.studentCount}</TableCell>
                      <TableCell empty={noData}>
                        {hasErrors ? (
                          <Text weight="semibold" color="lava">
                            {child.sisSyncOverview.errorCount}
                          </Text>
                        ) : (
                          child.sisSyncOverview.errorCount
                        )}
                      </TableCell>
                      <TableCell empty={noData}>{child.sisSyncOverview.alertCount}</TableCell>
                      {<td>{this.renderSyncStatus(syncStatus)}</td>}
                    </TableRow>
                  );
                })}
              </tbody>
            );
          }}
        </SmartTable>
      </section>
    );
  }
  render() {
    return <div className="AdminDistrictSisDashboard">{this.renderBody()}</div>;
  }
}

const withOrganization = graphql(ADMIN_DISTRICT_SIS_DASHBOARD_QUERY, {
  options: ({ orgId }) => ({
    variables: {
      organizationId: orgId,
    },
  }),
  props: ({ data }) => ({
    organization: idx(data, _ => _.organizations[0]),
    ...data,
  }),
});

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast, showAPIErrorToast }, dispatch);
}

export { AdminDistrictSisDashboard };
export default compose(
  withRouter,
  withOrganization,
  awaitData(['organization'], <LoadingIndicator height={400} size="small" />),
  // what does connect(undefined) do?
  connect(
    undefined,
    mapDispatchToProps
  )
)(AdminDistrictSisDashboard);
